import { useCallback, useEffect, useRef } from 'react'

type FormTrackingEvent =
  | 'form_start'
  | 'form_progress'
  | 'form_error'
  | 'form_submit'
  | 'form_abandonment'

interface FormTrackingPayload {
  event: FormTrackingEvent
  formId?: string
  formName: string
  [key: string]: any
}

const PROGRESS_THRESHOLDS = [25, 50, 75]

const pushToDataLayer = (payload: FormTrackingPayload) => {
  if (typeof window === 'undefined') return
  window.dataLayer = window.dataLayer || []
  window.dataLayer.push({
    timestamp: new Date().toISOString(),
    ...payload
  })
}

export function useFormTracking(formName: string, formId?: string) {
  const hasStarted = useRef(false)
  const hasFinished = useRef(false)
  const firedThresholds = useRef<Set<number>>(new Set())

  const trackStart = useCallback(() => {
    if (hasStarted.current) return
    hasStarted.current = true
    pushToDataLayer({ event: 'form_start', formName, formId })
  }, [formName, formId])

  const trackProgress = useCallback(
    (completedFields: number, totalFields: number) => {
      if (!totalFields) return
      const percentage = Math.round((completedFields / totalFields) * 100)
      PROGRESS_THRESHOLDS.forEach(threshold => {
        if (
          percentage >= threshold &&
          !firedThresholds.current.has(threshold)
        ) {
          firedThresholds.current.add(threshold)
          pushToDataLayer({
            event: 'form_progress',
            formName,
            formId,
            progress: threshold
          })
        }
      })
    },
    [formName, formId]
  )

  const trackError = useCallback(
    (errorFields?: string[]) => {
      pushToDataLayer({
        event: 'form_error',
        formName,
        formId,
        errorFields
      })
    },
    [formName, formId]
  )

  const trackSubmit = useCallback(() => {
    hasFinished.current = true
    pushToDataLayer({ event: 'form_submit', formName, formId })
  }, [formName, formId])

  useEffect(() => {
    const trackAbandonment = () => {
      if (hasStarted.current && !hasFinished.current) {
        hasFinished.current = true
        pushToDataLayer({ event: 'form_abandonment', formName, formId })
      }
    }

    window.addEventListener('beforeunload', trackAbandonment)
    return () => {
      trackAbandonment()
      window.removeEventListener('beforeunload', trackAbandonment)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return { trackStart, trackProgress, trackError, trackSubmit }
}

export default useFormTracking
